/**
 * Fair ROI Summary Component (Issue #679)
 *
 * Displays ROI calculation summary for a vendor at a specific fair.
 * Shows expenses, revenue, net profit, ROI percentage, and breakdowns.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { roiApi } from '../services/api';
import { formatCurrency, compareCurrency, isPositive } from '../utils/currency'; // Issue #905: Decimal.js utilities
import Decimal from 'decimal.js';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Type-safe error handling
import {
  FairROICalculation,
  EXPENSE_TYPE_LABELS,
  REVENUE_SOURCE_LABELS
} from '../types';

interface FairROISummaryProps {
  vendorId: number;
  fairId: number;
  refreshTrigger?: number; // Optional prop to trigger refresh
}

export const FairROISummary: React.FC<FairROISummaryProps> = ({
  vendorId,
  fairId,
  refreshTrigger
}) => {
  const [roi, setRoi] = useState<FairROICalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadROI = useCallback(async () => {
    try {
      setLoading(true);
      const data = await roiApi.getFairROI(vendorId, fairId);
      setRoi(data);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error loading ROI:', apiError);
    } finally {
      setLoading(false);
    }
  }, [vendorId, fairId]);

  useEffect(() => {
    loadROI();
  }, [loadROI, refreshTrigger]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8 text-gray-500">Loading ROI data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!roi) {
    return null;
  }

  const roiColor = roi.roi_percentage >= 0 ? 'text-green-600' : 'text-red-600';
  const roiBgColor = roi.roi_percentage >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">ROI Summary</h2>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Expenses */}
        <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
          <div className="text-sm font-medium text-secondary mb-1">Total Expenses</div>
          <div className="text-2xl font-bold text-ink">
            {formatCurrency(roi.total_expenses)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>

        {/* Total Revenue */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm font-medium text-green-600 mb-1">Total Revenue</div>
          <div className="text-2xl font-bold text-green-900">
            {formatCurrency(roi.total_revenue)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>

        {/* Net Profit */}
        <div className={`${roiBgColor} rounded-lg p-4 border`}>
          <div className={`text-sm font-medium ${roiColor} mb-1`}>Net Profit</div>
          <div className={`text-2xl font-bold ${roiColor}`}>
            {formatCurrency(roi.net_profit)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>

        {/* ROI Percentage */}
        <div className={`${roiBgColor} rounded-lg p-4 border`}>
          <div className={`text-sm font-medium ${roiColor} mb-1`}>ROI</div>
          <div className={`text-2xl font-bold ${roiColor}`} data-testid="roi-percentage">
            {Number(roi.roi_percentage).toFixed(1)}% {/* roi_percentage is number type */}
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Breakdown */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Expense Breakdown</h3>
          {Object.keys(roi.expense_breakdown).length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              No expenses recorded
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(roi.expense_breakdown)
                .sort((a, b) => compareCurrency(b[1], a[1])) // Issue #905: Decimal.js comparison for sorting
                .map(([type, amount]) => {
                  // Issue #1160: Decimal.js percentage calculation (amount / total) * 100
                  // Use Decimal.js for precision: (amount / total) * 100
                  const percentage = isPositive(roi.total_expenses)
                    ? new Decimal(amount).div(roi.total_expenses).times(100).toNumber()
                    : 0;
                  return (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {EXPENSE_TYPE_LABELS[type as keyof typeof EXPENSE_TYPE_LABELS] || type}
                        </div>
                        <div className="text-sm text-gray-500">
                          {percentage.toFixed(1)}% of total
                        </div>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(amount)} {/* Issue #905: Decimal.js formatting */}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Revenue Breakdown */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Revenue Breakdown</h3>
          {Object.keys(roi.revenue_breakdown).length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              No revenue recorded
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(roi.revenue_breakdown)
                .sort((a, b) => compareCurrency(b[1], a[1])) // Issue #905: Decimal.js comparison for sorting
                .map(([source, amount]) => {
                  // Issue #1160: Decimal.js percentage calculation (amount / total) * 100
                  // Use Decimal.js for precision: (amount / total) * 100
                  const percentage = isPositive(roi.total_revenue)
                    ? new Decimal(amount).div(roi.total_revenue).times(100).toNumber()
                    : 0;
                  return (
                    <div key={source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {REVENUE_SOURCE_LABELS[source as keyof typeof REVENUE_SOURCE_LABELS] || source}
                        </div>
                        <div className="text-sm text-gray-500">
                          {percentage.toFixed(1)}% of total
                        </div>
                      </div>
                      <div className="text-lg font-bold text-green-700">
                        {formatCurrency(amount)} {/* Issue #905: Decimal.js formatting */}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* ROI Explanation */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
          <h4 className="font-semibold text-ink mb-2">How ROI is Calculated</h4>
          <p className="text-sm text-ink">
            ROI (Return on Investment) = ((Total Revenue - Total Expenses) / Total Expenses) × 100
          </p>
          <p className="text-sm text-ink mt-2">
            A positive ROI means you made more money than you spent. A negative ROI means your expenses exceeded your revenue.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FairROISummary;
